import { hash_cyrb53 } from '../../js';

// Requirement: `#6990` - colour-coded chips for the `Shared With Franchises` column, so a
// franchise is recognisable at a glance and always wears the same colour.
//
// Colours are assigned by hand, keyed by franchise NAME (the report column carries names only).
// A computed hash cannot promise that two franchises never share a colour; with ~28 You Move Me
// franchises a fixed list can. Ranked by shared OTR jobs over the 12 months to 2026-09 (production):
// the 12 busiest get the soft chips on 12 distinct hues, the next 12 the solid chips, the rest the
// outline chips. Hues are rotated between tones so neighbours in the ranking never share one.
//
// A name missing from the list (a new or renamed franchise) still gets a stable colour from
// FALLBACK_CHIP_COLORS, which holds only slots no listed franchise uses, so it can never take a
// listed franchise's colour. To give it a hand-picked one, add a line below.
//
// The CSS for every `fc-<tone>-<hue>` class, in light and dark variants, is generated from
// `$franchise-chip-hues` in dashboard-otr-labor-tracking.component.scss.

export const FRANCHISE_CHIP_TONES = [ 'soft', 'solid', 'outline' ] as const;
export const FRANCHISE_CHIP_HUES = [
  'blue', 'orange', 'green', 'red', 'violet', 'teal',
  'yellow', 'pink', 'indigo', 'lime', 'cyan', 'brown',
] as const;

export type FranchiseChipTone = typeof FRANCHISE_CHIP_TONES[number];
export type FranchiseChipHue = typeof FRANCHISE_CHIP_HUES[number];

export interface FranchiseChipColor {
  tone: FranchiseChipTone;
  hue: FranchiseChipHue;
}

export interface FranchiseChip {
  name: string;
  colorClass: string;
}

export const FRANCHISE_CHIP_COLORS: Readonly<Record<string, FranchiseChipColor>> = {
  // Soft: the 12 franchises with the most shared jobs.
  'Kansas City': { tone: 'soft', hue: 'blue' },
  'Cincinnati': { tone: 'soft', hue: 'orange' },
  'Tulsa': { tone: 'soft', hue: 'green' },
  'St. Louis': { tone: 'soft', hue: 'red' },
  'Oklahoma City': { tone: 'soft', hue: 'violet' },
  'Denver': { tone: 'soft', hue: 'teal' },
  'Northwest Arkansas': { tone: 'soft', hue: 'yellow' },
  'Charlotte': { tone: 'soft', hue: 'pink' },
  'Indianapolis': { tone: 'soft', hue: 'indigo' },
  'Salt Lake City': { tone: 'soft', hue: 'lime' },
  'Lehigh Valley': { tone: 'soft', hue: 'cyan' },
  'Twin Cities': { tone: 'soft', hue: 'brown' },
  // Solid: the remaining franchises with shared jobs, then the rest of You Move Me A–Z.
  'Phoenix': { tone: 'solid', hue: 'teal' },
  'Portland': { tone: 'solid', hue: 'yellow' },
  'Interstate USA': { tone: 'solid', hue: 'pink' },
  'Honolulu': { tone: 'solid', hue: 'indigo' },
  'Edmonton': { tone: 'solid', hue: 'lime' },
  'Vancouver Island': { tone: 'solid', hue: 'cyan' },
  'Bay Area': { tone: 'solid', hue: 'brown' },
  'Calgary': { tone: 'solid', hue: 'blue' },
  'GTA Central': { tone: 'solid', hue: 'orange' },
  'Interstate CAN': { tone: 'solid', hue: 'green' },
  'Kitchener Waterloo': { tone: 'solid', hue: 'red' },
  'Miami': { tone: 'solid', hue: 'violet' },
  // Outline: the remaining You Move Me franchises.
  'Orange County': { tone: 'outline', hue: 'red' },
  'Reno-Tahoe': { tone: 'outline', hue: 'violet' },
  'Toledo': { tone: 'outline', hue: 'teal' },
  'Vancouver': { tone: 'outline', hue: 'yellow' },
};

const slotKey = (c: FranchiseChipColor) => `${c.tone}-${c.hue}`;

const ASSIGNED_SLOTS = new Set(Object.values(FRANCHISE_CHIP_COLORS).map(slotKey));

// Every slot no listed franchise uses, in a fixed order so the hash below stays stable.
export const FALLBACK_CHIP_COLORS: readonly FranchiseChipColor[] = FRANCHISE_CHIP_TONES
  .flatMap((tone) => FRANCHISE_CHIP_HUES.map((hue) => ({ tone, hue })))
  .filter((c) => !ASSIGNED_SLOTS.has(slotKey(c)));

export function franchiseChipColor(name: string): FranchiseChipColor {
  return FRANCHISE_CHIP_COLORS[name]
    ?? FALLBACK_CHIP_COLORS[hash_cyrb53(name) % FALLBACK_CHIP_COLORS.length];
}

// Splits the engine's `Shared With Franchises` display string (`sharedWithFranchisesExpr` joins
// with ', ') into chips. Display only: the row keeps the original string for the tooltip and the
// exports, so those still match the drill-down report character for character.
export function franchiseChips(sharedFranchises: string): FranchiseChip[] {
  return sharedFranchises
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => {
      const color = franchiseChipColor(name);
      return { name, colorClass: `fc-${color.tone}-${color.hue}` };
    });
}
